import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationService } from "../services/dataService";
import { AppNotification, NotificationType } from "../types";
import "./NotificationBell.css";

const POLL_MS = 60000;

export const NOTIF_ICON: Record<NotificationType, string> = {
  [NotificationType.OrderDeadlineSoon]: "⏳",
  [NotificationType.OrderOverdue]: "⚠",
  [NotificationType.StockLow]: "▦",
  [NotificationType.TaskAssigned]: "★",
  [NotificationType.DocumentSent]: "✓",
  [NotificationType.DocumentFailed]: "✕",
  [NotificationType.ReceivableOverdue]: "₺",
  [NotificationType.PurchaseOverdue]: "⇦",
  [NotificationType.ShipmentUndelivered]: "⇨",
};

export const relTime = (iso: string) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return "az önce";
  if (diff < 60) return `${Math.round(diff)} dk önce`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)} sa önce`;
  if (diff < 60 * 24 * 7) return `${Math.round(diff / 1440)} gün önce`;
  return d.toLocaleDateString("tr-TR");
};

/** Header zili: okunmamış sayacı, son 10 bildirim, tıklayınca okundu + ilgili kayda gider. */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try { setCount(await notificationService.unreadCount()); } catch { /* sessiz */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") refreshCount(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try { setItems(await notificationService.list(false, 10)); } catch { setItems([]); }
      finally { setLoading(false); }
      refreshCount();
    }
  };

  const openItem = async (n: AppNotification) => {
    if (!n.isRead) {
      try { await notificationService.markRead(n.notificationId); } catch { /* sessiz */ }
      setItems((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, isRead: true } : x)));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await notificationService.markAllRead(); } catch { return; }
    setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    setCount(0);
  };

  const removeItem = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    try { await notificationService.remove(n.notificationId); } catch { return; }
    setItems((prev) => prev.filter((x) => x.notificationId !== n.notificationId));
    if (!n.isRead) setCount((c) => Math.max(0, c - 1));
  };

  return (
    <div className="nb" ref={ref}>
      <button className={`nb-btn ${open ? "open" : ""}`} onClick={toggle} aria-label={`Bildirimler${count ? `, ${count} okunmamış` : ""}`} aria-haspopup="menu" aria-expanded={open} title="Bildirimler">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {count > 0 && <span className="nb-badge">{count > 99 ? "99+" : count}</span>}
      </button>

      {open && (
        <div className="nb-panel" role="menu">
          <div className="nb-head">
            <span className="nb-title">Bildirimler</span>
            {count > 0 && <button className="nb-link" onClick={markAll}>Tümünü okundu işaretle</button>}
          </div>
          <div className="nb-list">
            {loading && items.length === 0 && <div className="nb-empty">Yükleniyor…</div>}
            {!loading && items.length === 0 && <div className="nb-empty">Bildirim yok</div>}
            {items.map((n) => (
              <div key={n.notificationId} className={`nb-item ${n.isRead ? "" : "unread"} t${n.type}`} onClick={() => openItem(n)} role="menuitem" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") openItem(n); }}>
                <span className="nb-ico" aria-hidden="true">{NOTIF_ICON[n.type] || "•"}</span>
                <span className="nb-body">
                  <span className="nb-item-title">{n.title}</span>
                  {n.message && <span className="nb-item-msg">{n.message}</span>}
                  <span className="nb-item-meta">{n.typeName} · {relTime(n.createdAt)}</span>
                </span>
                <span className="nb-side">
                  {!n.isRead && <span className="nb-dot" aria-hidden="true" />}
                  <button className="nb-del" onClick={(e) => removeItem(e, n)} aria-label="Bildirimi sil" title="Sil">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                </span>
              </div>
            ))}
          </div>
          <div className="nb-foot">
            <button className="nb-link" onClick={() => { setOpen(false); navigate("/notifications"); }}>Tümünü gör ve tercihler</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
